package com.rainbowclubs.rainbow.presentation.admin

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.rainbowclubs.rainbow.data.models.Membership
import com.rainbowclubs.rainbow.data.models.MembershipDataItem
import com.rainbowclubs.rainbow.data.models.MembershipStatus
import com.rainbowclubs.rainbow.data.models.UserRole
import com.rainbowclubs.rainbow.data.requests.UpdateMembershipRequest
import com.rainbowclubs.rainbow.domain.use_cases.club.GetClubUseCase
import com.rainbowclubs.rainbow.domain.use_cases.membership.GetMembershipByIdUseCase
import com.rainbowclubs.rainbow.domain.use_cases.membership.UpdateMembershipUseCase
import com.rainbowclubs.rainbow.presentation.ScreenState
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch

class ExistingMemberDetailsViewModel(
    private val clubId: String,
    private val membershipId: String,
    private val getClubUseCase: GetClubUseCase,
    private val getMembershipByIdUseCase: GetMembershipByIdUseCase,
    private val updateMembershipUseCase: UpdateMembershipUseCase,
) : ViewModel() {

    data class UiState(
        val member: Membership,
        val status: MembershipStatus,
        val role: UserRole,
        val membershipDataFields: List<MembershipDataItem>,
        val membershipData: Map<String, String>,
        val validationErrors: Set<String> = emptySet(),
        val isUpdating: Boolean = false,
        val updateError: Boolean = false,
    ) {
        val hasChanges: Boolean
            get() = status != member.status.orUnknown() ||
                    role != member.role ||
                    membershipData != member.membershipData
    }

    sealed class NavigationEvent {
        object Back : NavigationEvent()
    }

    private val _screenState = MutableStateFlow<ScreenState<UiState>>(ScreenState.Loading)
    val screenState: StateFlow<ScreenState<UiState>> = _screenState.asStateFlow()

    private val navigationIntentChannel = Channel<NavigationEvent>(Channel.BUFFERED)
    val navigationIntent: Flow<NavigationEvent> = navigationIntentChannel.receiveAsFlow()

    init {
        fetchMember()
    }

    fun fetchMember() {
        viewModelScope.launch {
            _screenState.value = ScreenState.Loading
            getMembershipByIdUseCase(membershipId)
                .onSuccess { member ->
                    getClubUseCase(clubId)
                        .onSuccess { club ->
                            _screenState.value = ScreenState.Content(
                                member.toUiState(club.membershipData.orEmpty())
                            )
                        }
                        .onFailure {
                            _screenState.value = ScreenState.Error(it)
                        }
                }
                .onFailure {
                    _screenState.value = ScreenState.Error(it)
                }
        }
    }

    fun onStatusSelected(status: MembershipStatus) {
        updateContent { it.copy(status = status, updateError = false) }
    }

    fun onRoleSelected(role: UserRole) {
        updateContent { it.copy(role = role, updateError = false) }
    }

    fun onMembershipDataChanged(key: String, value: String) {
        updateContent {
            it.copy(
                membershipData = it.membershipData + (key to value),
                validationErrors = it.validationErrors - key,
                updateError = false,
            )
        }
    }

    fun updateMember() {
        val currentState = (_screenState.value as? ScreenState.Content)?.data ?: return
        if (!currentState.hasChanges || currentState.isUpdating) return

        val validationErrors = currentState.membershipDataFields
            .filter { it.required && currentState.membershipData[it.label].isNullOrBlank() }
            .map { it.label }
            .toSet()

        if (validationErrors.isNotEmpty()) {
            _screenState.value = ScreenState.Content(
                currentState.copy(
                    validationErrors = validationErrors,
                    updateError = false,
                )
            )
            return
        }

        viewModelScope.launch {
            _screenState.value = ScreenState.Content(
                currentState.copy(
                    isUpdating = true,
                    validationErrors = emptySet(),
                    updateError = false,
                )
            )

            updateMembershipUseCase(
                membershipId = membershipId,
                request = UpdateMembershipRequest(
                    status = currentState.status,
                    role = currentState.role,
                    membershipData = currentState.membershipData,
                )
            )
                .onSuccess { member ->
                    val latestState = (_screenState.value as? ScreenState.Content)?.data ?: currentState
                    _screenState.value = ScreenState.Content(
                        member.toUiState(latestState.membershipDataFields)
                    )
                }
                .onFailure {
                    val latestState = (_screenState.value as? ScreenState.Content)?.data ?: currentState
                    _screenState.value = ScreenState.Content(
                        latestState.copy(
                            isUpdating = false,
                            updateError = true,
                        )
                    )
                }
        }
    }

    fun onBackClick() {
        navigationIntentChannel.trySend(NavigationEvent.Back)
    }

    private fun updateContent(update: (UiState) -> UiState) {
        val currentState = (_screenState.value as? ScreenState.Content)?.data ?: return
        _screenState.value = ScreenState.Content(update(currentState))
    }

    private fun Membership.toUiState(membershipDataFields: List<MembershipDataItem>): UiState {
        return UiState(
            member = this,
            status = status.orUnknown(),
            role = role,
            membershipDataFields = membershipDataFields,
            membershipData = membershipData,
        )
    }
}

private fun MembershipStatus?.orUnknown(): MembershipStatus {
    return this ?: MembershipStatus.UNKNOWN
}
